/**
 * useGateFocus — keeps a student's place when a view replaces its own content
 * with a gate panel (a guardian has to turn it on, no profile yet, the feature
 * is off). Any view that swaps itself for a panel this way can use it; the
 * two chat advisors were the first.
 *
 * The panel REPLACES the content, so the control the student was using
 * unmounts and focus falls to <body>: a keyboard or screen-reader student
 * loses their place, and a freshly mounted role="status" panel isn't reliably
 * announced. So the panel's heading, a tabIndex={-1} element with the given
 * id, takes focus when the swap stranded the student, meaning both of:
 *
 *   - They were using the content: focus, or a pointer press, landed inside
 *     it at some point. Pointer presses count because Safari focuses no
 *     button on click.
 *   - Focus is now nowhere better: on <body>, or on a container of the panel.
 *     That is #main-content, where useRestoreFocus lands a closing dialog
 *     with no opener to return to (none, disabled, hidden or gone), and
 *     which a click on plain content inside it focuses from then on. Focus
 *     on a live control anywhere else is the student's own, and stays put.
 *
 * So a gate that lands on content nobody has used moves nothing: a pre-flight
 * read resolving on a fresh load, or while the student is still on the tab
 * they came in on. Checking only "is focus on <body>?" would pull focus into
 * the page on a fresh load, where nothing is focused yet.
 *
 * The move runs in a layout effect, in the commit that swapped the panel in,
 * so no key press can land on <body> in between, and a dialog opening in that
 * same commit records the heading as the place to return focus to.
 *
 * Pass the id only while its panel is rendered. The hook acts once, in the
 * commit where the id first appears; if something else is on screen then (a
 * loading spinner that outranks the panel), pass null until the panel shows.
 *
 *   const gateFocus = useGateFocus(gate ? HEADING_IDS[gate] : null);
 *   if (gate) return <GatePanel />; // renders the heading with that id
 *   return <div {...gateFocus.contentProps}>…</div>;
 */
import { useLayoutEffect, useRef } from 'react';

function useGateFocus(headingId) {
  const usedRef = useRef(false);

  useLayoutEffect(() => {
    if (!headingId || !usedRef.current) return;
    const heading = document.getElementById(headingId);
    if (!heading) return;
    const active = document.activeElement;
    // activeElement is null or in the document, never detached. <body> and
    // <html> contain the heading too, so one test covers them.
    if (active && !active.contains(heading)) return;
    heading.focus();
  }, [headingId]);

  const markUsed = () => {
    usedRef.current = true;
  };
  return { contentProps: { onFocus: markUsed, onPointerDown: markUsed } };
}

export default useGateFocus;
